import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Search, X, ArrowUpDown, ArrowUp, ArrowDown, TestTube, AlertCircle, LogIn } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { safeLog } from '@/utils/safeLogger';

interface UserWithSettings {
  id: string;
  email: string;
  created_at: string;
  use_test_stripe: boolean;
  has_subscription: boolean;
  subscription_tier: string | null;
  subscription_status: string | null;
}

export function TestUsersManager() {
  const [users, setUsers] = useState<UserWithSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof UserWithSettings>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [authError, setAuthError] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    safeLog.info('[TestUsersManager] Component mounted, loading users');
    
    // Check if user is logged in first
    const checkAuthAndLoad = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setAuthError(true);
        setLoading(false);
        return;
      }
      
      setAuthError(false);
      loadUsers();
    };
    
    checkAuthAndLoad();

    // Realtime updates for user_settings changes
    const channel = supabase
      .channel('test-users-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_settings'
        },
        () => {
          safeLog.info('[TestUsersManager] Settings changed via Realtime, reloading in 500ms...');
          setTimeout(() => loadUsers(), 500); // Give DB time to commit
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  async function loadUsers(retryCount = 0) {
    try {
      setLoading(true);
      
      safeLog.info('[TestUsersManager] Fetching users from edge function...');
      const { data, error } = await supabase.functions.invoke('get-all-users-with-settings');
      
      if (error) throw error;
      if (!data?.users) throw new Error('No users returned from edge function');

      setUsers(data.users);
      safeLog.info('[TestUsersManager] Loaded users successfully', { count: data.users.length });
    } catch (error) {
      console.error(`[TestUsersManager] Error loading users (attempt ${retryCount + 1}):`, error);
      
      // Retry up to 3 times with exponential backoff
      if (retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 500;
        safeLog.info(`[TestUsersManager] Retrying in ${delay}ms...`);
        setTimeout(() => loadUsers(retryCount + 1), delay);
        return;
      }
      
      toast({
        title: 'Error loading users',
        description: 'Could not load users. Click to retry.',
        variant: 'destructive',
        action: <Button variant="outline" size="sm" onClick={() => loadUsers()}>Retry</Button>
      });
    } finally {
      setLoading(false);
    }
  }

  async function toggleTestStripe(userId: string, currentValue: boolean) {
    const newValue = !currentValue;
    const originalValue = currentValue;
    
    safeLog.info(`[TestUsersManager] Toggling test stripe for user`, { userId, currentValue, newValue });
    
    // Optimistic update
    setUsers(prev => prev.map(u => 
      u.id === userId ? { ...u, use_test_stripe: newValue } : u
    ));

    try {
      const { data, error } = await supabase.functions.invoke('toggle-test-stripe', {
        body: {
          target_user_id: userId,
          enabled: newValue,
        },
      });

      if (error) throw error;

      // ✅ Edge function verifies using Service Role (bypasses RLS)
      if (!data?.verified) {
        safeLog.error('[TestUsersManager] Edge function verification failed');
        throw new Error(`Database verification failed: value not persisted correctly`);
      }

      safeLog.info('[TestUsersManager] ✅ Verified via edge function', { 
        userId, 
        value: newValue 
      });

      toast({
        title: 'Success',
        description: `Test Stripe ${newValue ? 'enabled' : 'disabled'} for user`,
      });

      // Reload after short delay to ensure DB commit
      setTimeout(() => loadUsers(), 500);
      
    } catch (error) {
      console.error('[TestUsersManager] Error toggling test stripe:', error);
      
      // Revert optimistic update
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, use_test_stripe: originalValue } : u
      ));
      
      toast({
        title: 'Error',
        description: 'Could not update test stripe setting. Click to retry.',
        variant: 'destructive',
        action: <Button variant="outline" size="sm" onClick={() => toggleTestStripe(userId, originalValue)}>Retry</Button>
      });
    }
  }

  // Filter & Sort Logic
  const filteredAndSorted = useMemo(() => {
    let result = [...users];
    
    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(u => 
        u.email?.toLowerCase().includes(term) ||
        u.subscription_tier?.toLowerCase().includes(term)
      );
    }
    
    // Sort
    result.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      if (aVal === null || aVal === undefined) return direction;
      if (bVal === null || bVal === undefined) return -direction;
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * direction;
      }
      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        return (aVal === bVal ? 0 : aVal ? -1 : 1) * direction;
      }
      return 0;
    });
    
    return result;
  }, [users, searchTerm, sortField, sortDirection]);

  const handleSort = (field: keyof UserWithSettings) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: keyof UserWithSettings }) => {
    if (sortField !== field) return <ArrowUpDown className="ml-2 h-4 w-4" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="ml-2 h-4 w-4" /> : 
      <ArrowDown className="ml-2 h-4 w-4" />;
  };

  return (
    <Card className="app-tile app-tile-hover">
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="app-icon-box">
            <TestTube className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle className="app-section-title">Test Users Management</CardTitle>
            <CardDescription>
              Configure which users should use Test Stripe (independent of admin role)
            </CardDescription>
          </div>
        </div>
        
        {authError && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>You need to log in with a Supabase account to access this feature.</span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/auth')}
                className="ml-4"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Log In
              </Button>
            </AlertDescription>
          </Alert>
        )}
        
        {!authError && (
          <>
            {/* Search Bar */}
            <div className="flex gap-2 mt-4">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email or subscription..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
              {searchTerm && (
                <Button variant="ghost" onClick={() => setSearchTerm('')}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            <p className="text-sm text-muted-foreground mt-2">
              {filteredAndSorted.length} of {users.length} users
              {filteredAndSorted.length !== users.length && ' (filtered)'}
            </p>
          </>
        )}
      </CardHeader>
      
      <CardContent>
        {authError ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>Please log in to view and manage test users.</p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : users.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">
            No users found
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center">
                      Email
                      <SortIcon field="email" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Registered
                      <SortIcon field="created_at" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('subscription_tier')}
                  >
                    <div className="flex items-center">
                      Subscription
                      <SortIcon field="subscription_tier" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('use_test_stripe')}
                  >
                    <div className="flex items-center">
                      Test Stripe
                      <SortIcon field="use_test_stripe" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSorted.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.email}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {format(new Date(user.created_at), 'dd.MM.yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      {user.has_subscription ? (
                        <div className="flex gap-2">
                          <Badge variant={user.subscription_status === 'active' ? 'default' : 'secondary'}>
                            {user.subscription_tier}
                          </Badge>
                          <Badge variant="outline">
                            {user.subscription_status}
                          </Badge>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">No subscription</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={user.use_test_stripe}
                          onCheckedChange={() => toggleTestStripe(user.id, user.use_test_stripe)}
                        />
                        {user.use_test_stripe && (
                          <Badge variant="default" className="bg-blue-500">
                            🧪 Test Mode
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
